import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Card } from './ui';
import { calendarDate, calendarParts, calendarShort, calendarSortKey } from '../lib/calendarDate';
import { Ga4HistoryHeaders, Ga4HistoryCells, Ga4HistoryTotals, Ga4HistoryNote, type Ga4HistoryRow } from './Ga4History';
import { DailyPageviewsHistory } from './DailyPageviewsHistory';

export interface HistoryRow extends Ga4HistoryRow {
  date?: string; id_yyyymmdd?: string; pageviews?: number; attempt?: number; cost?: number; percent_attempt?: number;
  pageviews_home?: number | string | null; pageviews_product?: number | string | null;
  pageviews_cart?: number | string | null; pageviews_purchase?: number | string | null;
  custom_pageviews?: Record<string, unknown> | string | null;
  branding_success?: number; branding_engaged?: number; branding_bounced?: number; branding_failed?: number;
  branding_duration_histogram?: unknown; branding_pages_histogram?: unknown; branding_ga_events?: unknown;
}

interface Props {
  rows: HistoryRow[];
  eventTypes?: string[];
  isAdmin: boolean;
  isBranding: boolean;
  maxAttempts?: number;
  maxAttemptPercent?: number;
  loading?: boolean;
}

const n = (value: unknown) => Number.isFinite(Number(value)) ? Number(value) : 0;
const dateValue = (row: HistoryRow) => row.date || row.id_yyyymmdd || '';
const rowKey = (row: HistoryRow) => String(calendarSortKey(dateValue(row)) || dateValue(row));
const attemptPercent = (row: HistoryRow) => n(row.pageviews) ? n(row.attempt) / n(row.pageviews) * 100 : n(row.percent_attempt);
const clicksPercent = (row: HistoryRow) => n(row.pageviews) ? n(row.clicks) / n(row.pageviews) * 100 : 0;
const successPercent = (row: HistoryRow) => n(row.clicks) ? n(row.branding_success) / n(row.clicks) * 100 : 0;
const cellClass = 'px-4 py-2.5 text-right whitespace-nowrap';

function SelectionBox({ label, rows, selected, onToggle }: {
  label: string; rows: HistoryRow[]; selected: Set<string>; onToggle: (rows: HistoryRow[]) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const count = rows.filter(row => selected.has(rowKey(row))).length;
  useEffect(() => { if (ref.current) ref.current.indeterminate = count > 0 && count < rows.length; }, [count, rows.length]);
  return <input ref={ref} type="checkbox" aria-label={label} checked={rows.length > 0 && count === rows.length}
    disabled={rows.length === 0} onChange={() => onToggle(rows)} onClick={event => event.stopPropagation()}
    className="h-4 w-4 cursor-pointer accent-brand-600 align-middle disabled:cursor-default" />;
}

function SummaryCells({ rows, isAdmin, isBranding, hasGa4 }: Pick<Props, 'rows' | 'isAdmin' | 'isBranding'> & { hasGa4: boolean }) {
  const available = rows.filter(row => !row.delivery_missing);
  const partial = available.length < rows.length ? '*' : '';
  const total = (key: keyof HistoryRow, money = false) => {
    if (!available.length) return '—';
    const value = available.reduce((sum, row) => sum + n(row[key]), 0);
    return (money ? `$${value.toFixed(2)}` : value.toLocaleString()) + partial;
  };
  const average = (value: (row: HistoryRow) => number, digits = 2) => available.length
    ? `${(available.reduce((sum, row) => sum + value(row), 0) / available.length).toFixed(digits)}%${partial}` : '—';
  const cell = (value: ReactNode, title?: string) => <td className={cellClass} title={title}>{value}</td>;
  const note = `Arithmetic average of daily percentages for ${available.length} available days.`;
  return <>
    {isBranding ? <>
      {cell(total('clicks'))}{cell(total('branding_success'))}{cell(total('branding_failed'))}
      {cell(total('branding_bounced'))}{cell(total('branding_engaged'))}{cell(average(successPercent, 1), note)}
      {cell('—')}{cell('—')}{cell('—')}
    </> : <>
      {cell(total('pageviews'))}
      {isAdmin && cell(total('attempt'))}
      {isAdmin && cell(average(attemptPercent), note)}
      {cell(total('clicks'))}
      {isAdmin && cell(average(clicksPercent), note)}
    </>}
    {cell(total('cost', true))}
    {hasGa4 && <Ga4HistoryTotals rows={rows} />}
  </>;
}

export function DailyClicksHistory({ rows, eventTypes, isAdmin, isBranding, maxAttempts, maxAttemptPercent, loading }: Props) {
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const selectedRows = rows.filter(row => selected.has(rowKey(row)));
  const hasGa4 = rows.some(row => row.ga4 != null);
  const columns = 1 + (isBranding ? 11 : isAdmin ? 7 : 4) + (hasGa4 ? 4 : 0);
  const groups = new Map<string, { label: string; rows: HistoryRow[] }>();
  for (const row of rows) {
    const parts = calendarParts(dateValue(row));
    const key = parts ? `${parts.year}-${parts.month}` : 'unknown';
    if (!groups.has(key)) {
      const label = parts ? new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
        .format(new Date(Date.UTC(parts.year, parts.month - 1, 1))) : 'Unknown month';
      groups.set(key, { label, rows: [] });
    }
    groups.get(key)!.rows.push(row);
  }

  // Preserve the selected dates during automatic refreshes, pruning days no longer present.
  useEffect(() => {
    const keys = new Set(rows.map(rowKey));
    setSelected(previous => [...previous].every(key => keys.has(key)) ? previous : new Set([...previous].filter(key => keys.has(key))));
  }, [rows]);

  function toggle(targets: HistoryRow[]) {
    setSelected(previous => {
      const next = new Set(previous);
      const remove = targets.every(row => previous.has(rowKey(row)));
      targets.forEach(row => remove ? next.delete(rowKey(row)) : next.add(rowKey(row)));
      return next;
    });
  }

  const summary = (summaryRows: HistoryRow[]) => <SummaryCells rows={summaryRows} isAdmin={isAdmin} isBranding={isBranding} hasGa4={hasGa4} />;
  const header = (label: string, detail?: string) => <th scope="col" className="px-4 py-2.5 font-medium text-right whitespace-nowrap">
    {label}{detail && <div className="normal-case font-normal text-[10px] text-slate-400">{detail}</div>}
  </th>;

  return <><Card className="overflow-hidden mb-6">
    <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-border">
      <div>
        <div className="text-sm font-semibold text-ink-900">Daily Clicks History</div>
        <div className="text-xs text-slate-500 mt-1">Totals and daily averages for percentages · select days or a month</div>
      </div>
      {selectedRows.length > 0 && <button type="button" onClick={() => setSelected(new Set())}
        className="text-xs font-medium text-brand-600 hover:underline">Clear selection</button>}
    </div>
    <div className="max-h-[70vh] overflow-auto" data-testid="daily-clicks-history" role="region" aria-label="Daily history scroll area" tabIndex={0}>
      <table className="w-full text-sm tabular-nums" aria-label="Daily Clicks History">
        <thead className="sticky top-0 z-10 bg-surface shadow-sm">
          <tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-surface-muted">
            <th scope="col" className="pl-4 pr-1 py-2.5 w-10"><SelectionBox label="Select all days" rows={rows} selected={selected} onToggle={toggle} /></th>
            <th scope="col" className="px-4 py-2.5 font-medium">Date</th>
            {isBranding ? <>
              {header('Clicks')}{header('Success')}{header('Failed')}{header('Bounced')}{header('Engaged')}{header('Success %')}
              {header('Duration Hist.')}{header('Pages Hist.')}{header('GA Events')}
            </> : <>
              {header('Pageviews')}
              {isAdmin && header('Attempt', `max ${maxAttempts != null ? n(maxAttempts).toLocaleString() : '100'}/min`)}
              {isAdmin && header('% Attempt', `max ${n(maxAttemptPercent)}%`)}
              {header('Clicks')}{isAdmin && header('% Clicks', 'of pageviews')}
            </>}
            {header('Cost')}{hasGa4 && <Ga4HistoryHeaders />}
          </tr>
          <tr data-testid="history-selection-summary" className="bg-brand-50 text-ink-900 font-semibold border-y border-brand-100">
            <td />
            <td className="px-4 py-3 whitespace-nowrap">
              <div aria-live="polite">Selected ({selectedRows.length})</div>
              <div className="text-[10px] font-normal text-slate-500">{selectedRows.length ? 'Totals / avg. %' : 'Select days below'}</div>
            </td>
            {summary(selectedRows)}
          </tr>
        </thead>
        {[...groups].map(([key, group]) => <tbody key={key} className="divide-y divide-border">
          <tr data-testid="history-month-summary" className="bg-surface-muted font-semibold text-ink-900 border-t-2 border-border">
            <td className="pl-4 pr-1 py-3"><SelectionBox label={`Select ${group.label}`} rows={group.rows} selected={selected} onToggle={toggle} /></td>
            <td className="px-4 py-3 whitespace-nowrap">{group.label}<div className="text-[10px] font-normal text-slate-500">{group.rows.length} {group.rows.length === 1 ? 'day' : 'days'} in period · totals / avg. %</div></td>
            {summary(group.rows)}
          </tr>
          {group.rows.map(row => {
            const chosen = selected.has(rowKey(row));
            const over = n(maxAttemptPercent) > 0 && attemptPercent(row) > n(maxAttemptPercent);
            const count = (key: keyof HistoryRow) => row.delivery_missing ? '—' : n(row[key]).toLocaleString();
            return <tr key={rowKey(row)} data-testid="history-day" onClick={() => toggle([row])}
              className={`cursor-pointer text-slate-700 ${chosen ? 'bg-brand-50' : 'hover:bg-surface-muted'}`}>
              <td className="pl-4 pr-1 py-2.5"><SelectionBox label={`Select ${calendarDate(dateValue(row))}`} rows={[row]} selected={selected} onToggle={toggle} /></td>
              <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{calendarShort(dateValue(row))}</td>
              {isBranding ? <>
                <td className={cellClass}>{count('clicks')}</td><td className={cellClass}>{count('branding_success')}</td>
                <td className={cellClass}>{count('branding_failed')}</td><td className={cellClass}>{count('branding_bounced')}</td>
                <td className={cellClass}>{count('branding_engaged')}</td>
                <td className={cellClass}>{row.delivery_missing ? '—' : `${successPercent(row).toFixed(1)}%`}</td>
                <td className="px-4 py-2.5 text-slate-400">{row.branding_duration_histogram ? '✓' : '-'}</td>
                <td className="px-4 py-2.5 text-slate-400">{row.branding_pages_histogram ? '✓' : '-'}</td>
                <td className="px-4 py-2.5 text-slate-400">{row.branding_ga_events ? '✓' : '-'}</td>
              </> : <>
                <td className={cellClass}>{count('pageviews')}</td>
                {isAdmin && <td className={cellClass}>{count('attempt')}</td>}
                {isAdmin && <td className={`${cellClass} ${over ? 'text-rose-600 font-semibold' : ''}`} title={over ? `Above the configured maximum (${maxAttemptPercent}%)` : undefined}>
                  {row.delivery_missing ? '—' : `${attemptPercent(row).toFixed(2)}%${over ? ' ▲' : ''}`}
                </td>}
                <td className={cellClass}>{count('clicks')}</td>
                {isAdmin && <td className={cellClass}>{row.delivery_missing ? '—' : `${clicksPercent(row).toFixed(2)}%`}</td>}
              </>}
              <td className={cellClass}>{row.delivery_missing ? '—' : `$${n(row.cost).toFixed(2)}`}</td>
              {hasGa4 && <Ga4HistoryCells row={row} />}
            </tr>;
          })}
        </tbody>)}
        {!rows.length && <tbody><tr><td colSpan={columns} className="px-4 py-8 text-center text-slate-400">{loading ? 'Loading…' : 'No data in this period.'}</td></tr></tbody>}
        {rows.length > 0 && <tfoot>
          <tr className="bg-surface-muted font-semibold text-ink-900 border-t-2 border-border">
            <td /><td className="px-4 py-3">Total<div className="text-[10px] font-normal text-slate-500">Daily avg. %</div></td>{summary(rows)}
          </tr>
        </tfoot>}
      </table>
    </div>
    {rows.some(row => row.delivery_missing) && <p className="px-4 py-3 text-xs text-slate-500 border-t border-border">* Delivery totals and averages include available days only.</p>}
    {hasGa4 && <Ga4HistoryNote />}
  </Card>
    {!isBranding && <DailyPageviewsHistory rows={rows} eventTypes={eventTypes} selectedRows={selectedRows} loading={loading} />}
  </>;
}
